import Vue from 'vue';
import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators';
import api, { beginAuthenticatedSession } from '@/service/api';
import User from '@/model/User';
import { UserAccountData } from '@/model/UserAccountData';
import eventBroadcaster from '@/service/eventBroadcaster';
import renderingNotificator from '@/service/renderingNotificator';
import ensureFacebookSdk from '@/service/facebookSdk';
import preloadAuthenticatedImages from '@/service/preloadAuthenticatedImages';

const { $vToastify: notifier } = new Vue() as any;

// keep resolver out of Vuex state to avoid serialization issues
let pendingRegistrationResolver: ((code: string) => Promise<void>) | null = null;

@Module({
    name: 'user',
})
export default class UserModule extends VuexModule {
    public loginState: 'authenticated'|'anonymous'|'unknown' = 'unknown';
    public loggedOut: boolean = false;

    public user: User | null = null;
    public redirectRoute: string = '/sessions';
    public provideCode: boolean = false;
    public usedStorageBytes: number = 0;

    public accountData: UserAccountData | null = null;

    public twoFactorTicket: {
        user: User;
    } | null = null;
    public twoFactorErrors: string[] = [];

    @Mutation
    public setLoggedInAs(user: User | null) {
        if (user) {
            this.loginState = 'authenticated';
            this.loggedOut = false;
        } else {
            this.loginState = 'anonymous';
        }

        this.user = user;
    }

    @Mutation
    public setRedirectRoute(route: string) {
        this.redirectRoute = route;
    }

    @Mutation
    public setLoggedOut(newValue: boolean) {
        this.loggedOut = newValue;
    }

    @Mutation
    public setAccountData(accountData: UserAccountData | null) {
        this.accountData = accountData;
    }

    @Mutation
    public resetRedirectRoute() {
        this.redirectRoute = '/sessions';
    }

    @Mutation
    public updateUser(newValues: Partial<User>) {
        if (!this.user) {
            throw new Error('User not loaded');
        }

        this.user = { ...this.user, ...newValues };
    }

    @Mutation
    public updateAccountData(newValues: Partial<UserAccountData>) {
        if (!this.accountData) {
            throw new Error('Account data not loaded');
        }

        this.accountData = { ...this.accountData, ...newValues };
    }

    @Mutation
    public setProvideCode(newValue: boolean) {
        this.provideCode = newValue;
    }

    @Mutation
    public setUsedStorageBytes(newValue: number) {
        this.usedStorageBytes = newValue;
    }

    @Mutation
    public require2FA(user: User) {
        this.twoFactorTicket = { user };
    }

    @Mutation
    public resolve2FA() {
        this.twoFactorTicket = null;
    }

    @Mutation
    public setTwoFactorErrors(errors: string[]) {
        this.twoFactorErrors = errors;
    }

    get twoFactorRequired() {
        return !!this.twoFactorTicket;
    }

    @Action({ rawError: true })
    public async setRegistrationResolver(resolver: (code: string) => Promise<void>) {
        pendingRegistrationResolver = resolver;
        this.setProvideCode(true);
    }

    @Action({ rawError: true })
    public async submitRegistrationCode(code: string) {
        if (!pendingRegistrationResolver) {
            throw new Error('No pending registration flow');
        }

        const resolver = pendingRegistrationResolver;
        await resolver(code);
        pendingRegistrationResolver = null;
        this.setProvideCode(false);
    }

    @Action({
        rawError: true,
    })
    public async authenticateFacebook(payload?: { code?: string; redirectOnSuccess?: boolean } | string) {
        const code = typeof payload === 'string' ? payload : payload?.code;
        const redirectOnSuccess = typeof payload === 'object' ? payload.redirectOnSuccess !== false : true;
        const facebookSdk = await ensureFacebookSdk();
        const { authResponse: { accessToken: fbToken }} = await new Promise<fb.StatusResponse>((resolve) => facebookSdk.login((response) => resolve(response), { scope: 'email' }));
        const loggedInUser = await api.authenticateFacebook(fbToken, code);

        await this.context.dispatch('loginAs', loggedInUser);
        if (redirectOnSuccess) {
            (await import('@/router')).default.push(this.redirectRoute);
            this.resetRedirectRoute();
        }
    }

    @Action({
        rawError: true,
    })
    public async authenticatePassword({
        email,
        password,
        redirectOnSuccess = true,
    }: {email: string; password: string; redirectOnSuccess?: boolean}) {
        const loggedInUser = await api.authenticatePassword(email, password);
        if (loggedInUser.twoFactorEnabled && !loggedInUser.twoFactorConfirmed) {
            this.require2FA(loggedInUser);
        } else {
            await this.context.dispatch('loginAs', loggedInUser);
            if (redirectOnSuccess) {
                (await import('@/router')).default.push(this.redirectRoute);
                this.resetRedirectRoute();
            }
        }
    }

    @Action({ rawError: true })
    public async authenticate2FA(payload: { code: string; redirectOnSuccess?: boolean } | string): Promise<boolean> {
        const code = typeof payload === 'string' ? payload : payload.code;
        const redirectOnSuccess = typeof payload === 'object' ? payload.redirectOnSuccess !== false : true;
        if (!this.twoFactorTicket) {
            throw new Error('No pending 2FA authentication');
        }
        this.setTwoFactorErrors([]);

        const data = await api.validate2FA(code);
        if ('error' in data && data.error) {
            if (data.errorCode === 'invalid_2fa_code') {
                this.setTwoFactorErrors(['Nieprawidłowy kod 2FA']);
                return false;
            }
            throw new Error(data.error);
        }
        if ('twoFactorValidUntil' in data) {
            await this.context.dispatch('loginAs', this.twoFactorTicket.user);
            this.updateUser({ twoFactorValidUntil: data.twoFactorValidUntil });
            this.resolve2FA();
            if (redirectOnSuccess) {
                (await import('@/router')).default.push(this.redirectRoute);
                this.resetRedirectRoute();
            }
            return true;
        }

        return false;
    }

    @Action({ rawError: true })
    public async logout() {
        await api.logout();
        await this.context.dispatch('loginAs', null);
        (await import('@/router')).default.push({ name: 'home' });
    }

    @Action({
        rawError: true,
    })
    public async loginAs(user: User | null) {
        if (user && (!this.user || this.loggedOut || this.user.id !== user.id)) {
            beginAuthenticatedSession();
        }
        this.setLoggedInAs(user);
        if (user) {
            preloadAuthenticatedImages();
            this.context.dispatch('connectChat');
            // hack to fetch all user chats
            this.context.dispatch('loadUserSessionList', user.id);
            this.context.dispatch('fetchWelcomeMessageSets');
            eventBroadcaster.run();
            this.context.dispatch('loadAccountData');
            renderingNotificator.run();
            try {
                this.setUsedStorageBytes(await api.getUsedStorageBytes());
            } catch (_) {
                this.setUsedStorageBytes(0);
            }
        } else {
            this.context.dispatch('disconnectChat');
            this.context.dispatch('resetWelcomeMessageSets');
            eventBroadcaster.stop();
            this.setAccountData(null);
            renderingNotificator.stop();
        }
    }

    @Action({ rawError: true })
    public async resolveLoginState() {
        await this.context.dispatch('loginAs', await api.getUser());
    }

    @Action({ rawError: true })
    public async loadAccountData() {
        const accountData = await api.getUserData();
        this.setAccountData(accountData);
    }

    @Action({ rawError: true })
    public async changeStudioName(newName: string) {
        await api.updateStudioName(newName);
        this.updateUser({
            studioName: newName,
        });
        notifier.success({
            title: 'Zaktualizowano',
            body: 'Nazwa fotografa została zmieniona',
        });
    }

    @Action({ rawError: true })
    public async updateLastSeenTutorialVersion(newVersion: number) {
        await api.updateLastSeenTutorialVersion(newVersion);
        this.updateUser({
            lastSeenTutorialVersion: newVersion,
        });
    }

    @Action({ rawError: true })
    public async refreshUsedStorageBytes() {
        const usedStorageBytes = await api.getUsedStorageBytes();
        this.setUsedStorageBytes(usedStorageBytes);
    }

    @Action({ rawError: true })
    public async updatePublicationConsent(publicationConsent: boolean) {
        // TODO: Add API call when backend is ready
        // await api.updatePublicationConsent(publicationConsent);
        this.updateUser({
            publicationConsent,
        } as Partial<User>);
        if (this.accountData) {
            this.updateAccountData({ publicationConsent });
        }
        notifier.success({
            title: 'Zaktualizowano',
            body: 'Zgody na publikację zostały ' + (publicationConsent ? 'włączone' : 'wyłączone'),
        });
    }

    @Action({ rawError: true })
    public async updatePaymentReceiving(paymentReceiving: boolean) {
        // TODO: Add API call when backend is ready
        // await api.updatePaymentReceiving(paymentReceiving);
        this.updateUser({
            paymentReceiving,
        } as Partial<User>);
        if (this.accountData) {
            this.updateAccountData({ receivePayments: paymentReceiving });
        }
        notifier.success({
            title: 'Zaktualizowano',
            body: 'Otrzymywanie płatności zostało ' + (paymentReceiving ? 'włączone' : 'wyłączone'),
        });
    }

    @Action({ rawError: true })
    public async mark2FAEnabled(twoFactorMethod: User['twoFactorMethod']) {
        if (!this.user) {
            throw new Error('User not loaded');
        }

        this.updateUser({
            twoFactorEnabled: true,
            twoFactorMethod,
        });
    }
}
